import '@assets/chrome-bug.css'
import '@assets/main.scss'
import Head from '@components/common/Head'
import Layout from '@components/common/Layout'
import { fetchGlobalData } from '@redux/globalSlice'
import { wrapper } from '@redux/store'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { SessionProvider } from 'next-auth/react'
import NextTopLoader from 'nextjs-toploader'

import { queryConfig } from '@lib/react-query'
import App, { AppProps } from 'next/app'
import localFont from 'next/font/local'
import { useEffect } from 'react'
import { Provider } from 'react-redux'

import AuthProvider from '@components/common/Provider/AuthProvider'
import { ToastQueueProvider } from '@components/ui/ToastQueue'
import VividWorksProvider from '@lib/vividworks/vivid-works-provider'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import React from 'react'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/thumbs'

const helvetical = localFont({
  src: '../assets/fonts/helvetical.woff2',
  variable: '--helvetica',
  fallback: ['Helvetica Neue LT Std Roman', 'Helvetica', 'Arial', 'sans-serif'],
})
const helveticalMd = localFont({
  src: '../assets/fonts/helvetical.woff2',
  variable: '--helvetica-md',
  fallback: ['Helvetica Neue LT Std Roman', 'Helvetica', 'Arial', 'sans-serif'],
})
const kingscaslon = localFont({
  src: [
    {
      path: '../assets/fonts/kingscaslon-regular.woff2',
      style: 'normal',
    },
    {
      path: '../assets/fonts/kingscaslon-italic.woff2',
      style: 'italic',
    },
  ],
  variable: '--kingscaslon',
  fallback: ['Kings Caslon Display Typo', 'Times New Roman', 'Times', 'serif'],
})
const lato = localFont({
  src: '../assets/fonts/lato-regular.ttf',
  variable: '--lato',
})

function MyApp({
  Component,
  pageProps: { session, ...pageProps },
  ...rest
}: AppProps) {
  const router = useRouter()

  const slug = router.asPath.split('?')[0]

  const { store, props } = wrapper.useWrappedStore(rest)

  useEffect(() => {
    document.body.classList?.remove('loading')
  }, [])

  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      }),
  )

  if (rest.router.pathname.startsWith('/arview/')) {
    return <Component key={slug} {...pageProps} />
  }

  return (
    <>
      <style jsx global>
        {`
          :root {
            --helvetica: ${helvetical.style.fontFamily};
            --helvetica-md: ${helveticalMd.style.fontFamily};
            --kingscaslon: ${kingscaslon.style.fontFamily};
            --lato: ${lato.style.fontFamily};
          }
        `}
      </style>
      <NextTopLoader
        shadow={false}
        showSpinner={false}
        height={2}
        color="rgba(0,0,0,0.5)"
        template='
        <div class="bar" role="bar">
          <div class="peg"></div>
        </div>'
      />
      <SessionProvider session={session}>
        <AuthProvider>
          <ToastQueueProvider max={5}>
            <QueryClientProvider client={queryClient}>
              <Provider store={store}>
                <Head pageProps={pageProps as any} />
                <Layout pageProps={pageProps as any}>
                  <Component key={slug} {...pageProps} />
                </Layout>

                <VividWorksProvider router={rest.router} />
              </Provider>
              <ReactQueryDevtools buttonPosition="bottom-left" />
            </QueryClientProvider>
          </ToastQueueProvider>
        </AuthProvider>
      </SessionProvider>
    </>
  )
}
MyApp.getInitialProps = wrapper.getInitialAppProps(
  (store) => async (context) => {
    if (context.router.pathname.startsWith('/arview/')) return { pageProps: {} }

    await store.dispatch(fetchGlobalData(context))

    return {
      pageProps: {
        ...(await App.getInitialProps(context)).pageProps,
      },
    }
  },
)

export default MyApp
